/**
 * homeView.ts — pure view logic for Sales Home (redesign slice 3, the
 * calm-minimal Minimal board; ADOPTION.md §15, register row "Redesign slice 3").
 * Component-free so vitest pins every rule (the dashboardView / closeFlow
 * pattern). Every figure here is DERIVED from a read path the page already
 * has — the six-month revenue series, the deals list and the activities list
 * that fetchSalesSummary returns — and nothing is typed in or fabricated.
 * Money formatting is injected (fmt) so this module has no component import.
 */
import type { SalesDeal, SalesActivity } from '../../types'
import { topOpenDeals } from './dashboardView'
import type { DeltaTone } from './trendDeltas'

const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MONTH_SHORT_UPPER = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** "2026-08" → "August" — a fixed table over the wire's bare key; no Date
 *  parsing, no timezone (the same rule monthLabel keeps for "Aug"). */
export function monthLong(key: string): string {
  return MONTH_LONG[Number(key.slice(5, 7)) - 1] ?? key
}

/** The greeting's date line — the boards' "Saturday, 26 September": weekday,
 *  day and month in the BROWSER's local time (the visual suite pins the
 *  browser clock, so the line is deterministic there). No year: it is today. */
export function dateLine(now: Date): string {
  return `${WEEKDAY[now.getDay()]}, ${now.getDate()} ${MONTH_LONG[now.getMonth()]}`
}

// ── Won this month — the FACT delta, RULED §22.7 (2026-09-26) ──────────────
// 1. derived from wonPence, never from the rendered labels (147, not 148);
// 2. the label says the current month is PARTIAL ("September to date");
// 3. a zero prior renders ABSENT, never a percentage over zero;
// 4. no copy implies the figure is fixed — "to date", nothing final.
export type WonDelta =
  | { kind: 'delta'; pct: number; tone: Exclude<DeltaTone, 'absent'>; prevPence: number; prevMonth: string; currentMonth: string }
  | { kind: 'absent'; reason: 'no-prior' | 'zero-prior'; prevMonth: string | null; currentMonth: string | null }

/** The series is the wire's six zero-filled buckets, ascending, last = the
 *  current month (revenueByMonthView pins that shape). */
export function wonDelta(series: { month: string; won: number }[]): WonDelta {
  const n = series.length
  if (n < 2) return { kind: 'absent', reason: 'no-prior', prevMonth: null, currentMonth: n ? series[n - 1].month : null }
  const cur = series[n - 1], prev = series[n - 2]
  if (prev.won === 0) return { kind: 'absent', reason: 'zero-prior', prevMonth: prev.month, currentMonth: cur.month }
  const pct = Math.round(((cur.won - prev.won) / prev.won) * 100)
  return { kind: 'delta', pct, tone: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat', prevPence: prev.won, prevMonth: prev.month, currentMonth: cur.month }
}

/**
 * The Won-this-month card's line. A zero prior says WHICH month it cannot
 * compare against and claims nothing about why: §22.7 (3) asks the copy to
 * tell "no won deals in August" from "before this tenant existed" by the
 * tenant's creation date, and no read path carries that date (GET /v1/me:
 * subject, provider, role, tenantId, tenantName, features — measured
 * 2026-09-27), so neither claim is made until it does.
 */
export function wonDeltaLine(d: WonDelta, fmt: (pence: number) => string): { text: string; tone: DeltaTone } {
  if (d.kind === 'delta') {
    const arrow = d.tone === 'up' ? '↑' : d.tone === 'down' ? '↓' : '→'
    return { tone: d.tone, text: `${arrow} ${Math.abs(d.pct)}% vs ${fmt(d.prevPence)} in ${monthLong(d.prevMonth)} · ${monthLong(d.currentMonth)} to date` }
  }
  const to = d.currentMonth ? ` · ${monthLong(d.currentMonth)} to date` : ''
  if (d.reason === 'zero-prior' && d.prevMonth) return { tone: 'absent', text: `No ${monthLong(d.prevMonth)} figure to compare against${to}` }
  return { tone: 'absent', text: `No prior month on the wire${to}` }
}

// ── The greeting's insight sentence — a runtime template over two facts ──────
const WORD = ['', 'one', 'two', 'three']

export function insightSentence(input: { wonPence: number; deals: SalesDeal[]; firstStage: string }, fmt: (pence: number) => string): string {
  const first = input.wonPence > 0 ? `${fmt(input.wonPence)} won so far this month.` : 'Nothing won yet this month.'
  const top = topOpenDeals(input.deals, 3)
  if (top.length === 0) return `${first} No open deals in the pipeline.`
  const allFirst = top.every(d => d.stage === input.firstStage)
  if (allFirst) {
    return top.length === 1
      ? `${first} Your largest deal is still at ${input.firstStage}.`
      : `${first} Your ${WORD[top.length]} largest deals are still at ${input.firstStage}.`
  }
  return `${first} Your largest open deal, ${top[0].name}, is at ${top[0].stage}.`
}

// ── Largest open deal and its share of the pipeline ─────────────────────────
/** A share is one division on the GBP-comparable leg: basePence over the
 *  fenced open Σ. An uncaptured row (basePence null) has no honest share. */
export function sharePct(basePence: number | null | undefined, totalPipelinePence: number): number | null {
  if (basePence == null || !(totalPipelinePence > 0)) return null
  return Math.round((basePence / totalPipelinePence) * 100)
}

export const DEALS_LIST_CAP = 200

/** The largest OPEN deal of the list the page fetched — which is the newest
 *  200 (the D4-3 ceiling dashboardView names). Under the cap the figure is
 *  the tenant's largest; AT the cap it is the largest of the newest 200 and
 *  the note says so (the cap-sentence family). */
export function largestOpenDeal(deals: SalesDeal[], totalPipelinePence: number, cap = DEALS_LIST_CAP): { deal: SalesDeal; sharePct: number | null; capped: boolean } | null {
  const [deal] = topOpenDeals(deals, 1)
  if (!deal) return null
  return { deal, sharePct: sharePct(deal.basePence, totalPipelinePence), capped: deals.length >= cap }
}

export function largestDealNote(r: { deal: SalesDeal; sharePct: number | null; capped: boolean }): string {
  const share = r.sharePct == null ? 'share not capturable (uncaptured currency)' : `${r.sharePct}% of pipeline`
  return `${r.deal.company} · ${share}${r.capped ? ` · largest of the newest ${DEALS_LIST_CAP}` : ''}`
}

// ── Needs attention — a rule over the list: open value waiting at the first stage ──
export interface Attention { pence: number; count: number; companies: string[]; excluded: number }

export function needsAttention(deals: SalesDeal[], firstStage: string): Attention | null {
  const rows = deals.filter(d => d.stage === firstStage)
  if (rows.length === 0) return null
  const captured = rows.filter(d => d.basePence != null).sort((a, b) => (b.basePence ?? 0) - (a.basePence ?? 0))
  const companies: string[] = []
  for (const d of captured) { const c = d.company.trim(); if (c && !companies.includes(c)) companies.push(c) }
  for (const d of rows) { const c = d.company.trim(); if (c && !companies.includes(c)) companies.push(c) }
  return { pence: captured.reduce((s, d) => s + (d.basePence ?? 0), 0), count: rows.length, companies, excluded: rows.length - captured.length }
}

export function joinNames(names: string[], max = 3): string {
  const shown = names.slice(0, max)
  const rest = names.length - shown.length
  if (shown.length === 0) return ''
  if (rest > 0) return `${shown.join(', ')} and ${rest} more`
  if (shown.length === 1) return shown[0]
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
}

export function attentionCopy(a: Attention, firstStage: string, fmt: (pence: number) => string): { headline: string; body: string } {
  const headline = a.pence > 0
    ? `${fmt(a.pence)} is still at ${firstStage}`
    : `${a.count} ${a.count === 1 ? 'deal is' : 'deals are'} still at ${firstStage}`
  const plural = a.companies.length > 1 || (a.companies.length === 1 && a.count > 1)
  const body = a.companies.length
    ? `${joinNames(a.companies)} ${plural ? "haven't" : "hasn't"} been qualified yet.`
    : `${a.count} ${a.count === 1 ? 'deal has' : 'deals have'} not been qualified yet.`
  return { headline, body }
}

// ── Up next — the earliest PENDING activity with a due date ─────────────────
export interface UpNext {
  next: { activity: SalesActivity; overdue: boolean } | null
  pendingCount: number
  pendingWithoutDue: number
}

export function upNext(activities: SalesActivity[], nowMs: number): UpNext {
  const pending = activities.filter(a => !a.done)
  const due = pending.filter(a => a.dueAt).sort((a, b) => Date.parse(a.dueAt!) - Date.parse(b.dueAt!))
  const first = due[0]
  return {
    next: first ? { activity: first, overdue: Date.parse(first.dueAt!) < nowMs } : null,
    pendingCount: pending.length,
    pendingWithoutDue: pending.length - due.length,
  }
}

export function dateBox(iso: string): { mon: string; day: string } {
  const d = new Date(iso)
  return { mon: MONTH_SHORT_UPPER[d.getMonth()], day: String(d.getDate()) }
}

/** "Logged 7 days ago" — whole days on the browser clock. */
export function loggedAgo(createdAt: string, nowMs: number): string {
  const days = Math.floor((nowMs - Date.parse(createdAt)) / 86_400_000)
  if (!Number.isFinite(days) || days < 0) return 'Logged just now'
  if (days === 0) return 'Logged today'
  if (days === 1) return 'Logged yesterday'
  return `Logged ${days} days ago`
}

export function initials(name: string): string {
  return name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
}
